(function () {
  "use strict";

  const root = document.querySelector('[data-vf-11mode-landing]');
  if (!root) return;

  document.body.classList.add('vf-11mode-page');
  document.getElementById('header-group')?.setAttribute('hidden', 'hidden');

  const prefersReducedMotion = (() => {
    try {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  })();

  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function rand(min, max) { return min + Math.random() * (max - min); }

  // ------------------------------
  // Scroll hero progress -> CSS variable
  // (same mechanic as vf_p3-landing.js)
  // ------------------------------

  const hero = root.querySelector('[data-scroll-hero]');
  const heroStage = root.querySelector('[data-hero-stage]');
  const heroContent = root.querySelector('[data-hero-content]');
  const heroVideo = root.querySelector('[data-hero-video]');
  let heroVideoStarted = false;
  let heroVideoCanPlay = true;

  let heroTopY = 0;
  let heroRunway = 1;

  // Video <source media> is selected on load, not continuously on resize.
  // Explicitly switch at the breakpoint, keeping the corresponding poster.
  if (heroVideo) {
    const narrowHero = window.matchMedia('(max-width: 900px)');
    const sources = heroVideo.querySelectorAll('source');
    let resumeTime = 0;
    heroVideo.addEventListener('loadedmetadata', () => {
      if (resumeTime > 0 && Number.isFinite(heroVideo.duration)) {
        heroVideo.currentTime = Math.min(resumeTime, Math.max(0, heroVideo.duration - 0.1));
      }
      resumeTime = 0;
      updateHeroProgress();
    });
    function selectHeroSource() {
      const source = sources[narrowHero.matches ? 0 : 1];
      if (!source) return;
      heroVideo.poster = narrowHero.matches ? heroVideo.dataset.posterSquare : heroVideo.dataset.posterWide;
      if (heroVideo.currentSrc === source.src || heroVideo.src === source.src) return;
      resumeTime = heroVideo.currentTime || 0;
      heroVideoStarted = false;
      heroVideo.closest('.video-hero__media')?.classList.remove('is-playing');
      heroVideo.src = source.src;
      heroVideo.load();
    }
    narrowHero.addEventListener('change', selectHeroSource);
    selectHeroSource();
  }


  function computeHeroMetrics() {
    if (!hero) return;
    const rect = hero.getBoundingClientRect();
    heroTopY = window.scrollY + rect.top;
    heroRunway = Math.max(1, hero.offsetHeight - window.innerHeight);
    if (heroStage) {
      heroStage.style.setProperty('--hero-runway-px', `${heroRunway}px`);
    }
  }

  function updateHeroProgress() {
    if (!hero || !heroStage) return;

    const raw = clamp((window.scrollY - heroTopY) / heroRunway, 0, 1);

    // Piecewise remap: ramp faster early so copy clears sooner,
    // slow down late so full-bleed video holds longer.
    let p;
    if (raw < 0.22) {
      p = (raw / 0.22) * 0.62;
    } else {
      p = 0.62 + ((raw - 0.22) / 0.78) * 0.38;
    }
    p = clamp(p, 0, 1);

    heroStage.style.setProperty('--hero-progress', String(p));
    heroStage.style.setProperty('--hero-progress-raw', String(raw));

    // Start video on first scroll into the hero runway.
    if (heroVideo && heroVideoCanPlay && !heroVideoStarted && p > 0.01) {
      heroVideoStarted = true;
      const playPromise = heroVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay blocked: poster frame still looks fine.
        });
      }
    }

    const mediaEl = heroVideo ? heroVideo.closest('.video-hero__media') : null;
    if (mediaEl && heroVideo) {
      const isPlaying = !!(heroVideo.currentTime > 0 && !heroVideo.paused && !heroVideo.ended);
      mediaEl.classList.toggle('is-playing', isPlaying);
    }

    if (heroVideo && heroVideoStarted && p < 0.001) {
      heroVideoStarted = false;
      try { heroVideo.pause(); } catch {}
    }

    document.body.classList.toggle('has-collab-header', p >= 0.67);

    if (heroContent) {
      heroContent.classList.toggle('is-dim', p > 0.45);
    }
  }

  // Pause hero video when mostly off-screen
  if (heroStage && heroVideo && 'IntersectionObserver' in window) {
    const heroIO = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const visibleRatio = entry.intersectionRatio || 0;
      heroVideoCanPlay = visibleRatio >= 0.4;
      if (!heroVideoCanPlay) {
        try { heroVideo.pause(); } catch {}
        heroVideoStarted = false;
      }
    }, { threshold: [0, 0.2, 0.35, 0.5, 1] });
    heroIO.observe(heroStage);
  }

  window.addEventListener('scroll', updateHeroProgress, { passive: true });
  window.addEventListener('resize', () => {
    computeHeroMetrics();
    updateHeroProgress();
  });

  // ------------------------------
  // How-it-works animation video: play only while in view
  // ------------------------------

  const howVideo = root.querySelector('[data-how-video]');
  if (howVideo && 'IntersectionObserver' in window) {
    const howIO = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        const p = howVideo.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        try { howVideo.pause(); } catch {}
      }
    }, { threshold: 0.25 });
    howIO.observe(howVideo);
  }

  // ------------------------------
  // Valve demo: interactive "goes to 11" panel
  // ------------------------------

  const demoSection = root.querySelector('[data-valve-demo]');
  const demoPanel = root.querySelector('[data-valve-panel]');
  const statusEl = root.querySelector('[data-valve-status]');
  const readoutEl = root.querySelector('[data-volume-readout]');
  const segs = Array.from(root.querySelectorAll('[data-seg]'));
  const btnOpen = root.querySelector('[data-valve-open]');
  const btnClose = root.querySelector('[data-valve-close]');

  const demo = {
    mode: 'stock',       // 'stock' | 'eleven'
    volume: 6,           // displayed value (float)
    target: 6,
    nextWanderAt: 0,
    active: false,
    raf: 0,
    lastLit: -1,
    lastReadout: -1,
  };

  // ------------------------------
  // Sound: two real clips (valves closed / valves fully open) from 11MODE's
  // G87 M2 short. Plain <audio> only — no Web Audio, so iOS treats it like any
  // media element. The meter follows a precomputed 20 Hz loudness envelope
  // (data-envelope on each <audio>) mapped into the mode's band, so what you
  // see tracks what you hear without decoding anything client-side.
  // ------------------------------

  const soundRow = root.querySelector('[data-valve-sound]');
  const soundBtn = root.querySelector('[data-sound-toggle]');
  const soundDesc = root.querySelector('[data-sound-desc]');
  const kindBtns = Array.from(root.querySelectorAll('[data-sound-kind]'));
  // Clips keyed "<mode>:<kind>" — stock/eleven × idle/rev. Idle pair is the
  // G87 M2, rev pair is the 8Y RS3 (each pair from one take, same car + mic).
  const clips = {};
  for (const el of root.querySelectorAll('[data-sound-clip]')) clips[el.dataset.soundClip] = el;
  const ENVELOPE_RATE = 20; // samples per second
  // Meter band per mode: factory never clears 7; 11 Mode lives at the top.
  const BAND = { stock: [1, 7], eleven: [8, 11] };

  function parseEnvelope(el) {
    try {
      const arr = JSON.parse(el?.dataset.envelope || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }
  const envelopes = {};
  for (const key of Object.keys(clips)) envelopes[key] = parseEnvelope(clips[key]);

  const sound = { playing: false, kind: 'idle' };

  function clipKey() { return `${demo.mode}:${sound.kind}`; }
  function currentClip() { return clips[clipKey()] || null; }

  const DESC = {
    'stock:idle': 'Valves closed — factory idle',
    'stock:rev': 'Valves closed — factory revs',
    'eleven:idle': 'Valves fully open — 11 Mode idle',
    'eleven:rev': 'Valves fully open — 11 Mode revs',
  };

  function setSoundUI() {
    const playing = sound.playing;
    soundBtn?.setAttribute('aria-pressed', String(playing));
    soundBtn?.setAttribute('aria-label', playing ? 'Pause exhaust sound' : 'Play exhaust sound');
    soundRow?.classList.toggle('is-playing', playing);
    for (const b of kindBtns) b.setAttribute('aria-pressed', String(b.dataset.soundKind === sound.kind));
    if (soundDesc) soundDesc.textContent = DESC[clipKey()] || '';
  }

  function setKind(kind) {
    if (sound.kind === kind || !kindBtns.length) return;
    sound.kind = kind;
    if (sound.playing) playSound(); // hand off mid-play, same as a remote press
    else setSoundUI();
  }

  for (const b of kindBtns) b.addEventListener('click', () => setKind(b.dataset.soundKind));

  function stopSound() {
    for (const el of Object.values(clips)) {
      if (!el) continue;
      el.pause();
      try { el.currentTime = 0; } catch { /* not seekable yet */ }
    }
    sound.playing = false;
    soundBtn?.classList.remove('is-loading');
    setSoundUI();
  }

  function playSound() {
    const el = currentClip();
    if (!el) return;
    // Silence the other clip first so a mode swap mid-play is a clean cut.
    for (const other of Object.values(clips)) {
      if (other && other !== el) { other.pause(); try { other.currentTime = 0; } catch { /* noop */ } }
    }
    try { el.currentTime = 0; } catch { /* noop */ }
    sound.playing = true;
    soundBtn?.classList.add('is-loading');
    setSoundUI();
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(() => soundBtn?.classList.remove('is-loading'))
       .catch(() => stopSound()); // blocked or failed: fall back to the simulated meter
    } else {
      soundBtn?.classList.remove('is-loading');
    }
  }

  // Envelope -> meter level for the clip that is currently audible.
  function soundLevel() {
    const el = currentClip();
    const env = envelopes[clipKey()] || [];
    if (!sound.playing || !el || el.paused || !env.length) return null;
    const i = clamp(Math.floor(el.currentTime * ENVELOPE_RATE), 0, env.length - 1);
    const [lo, hi] = BAND[demo.mode];
    return lo + env[i] * (hi - lo);
  }

  soundBtn?.addEventListener('click', () => {
    if (sound.playing) stopSound();
    else playSound();
  });

  for (const el of Object.values(clips)) {
    if (!el) continue;
    el.addEventListener('ended', () => { if (sound.playing && el === currentClip()) stopSound(); });
    el.addEventListener('error', () => { if (sound.playing && el === currentClip()) stopSound(); });
  }

  function setMode(mode) {
    if (demo.mode === mode) return;
    demo.mode = mode;

    const eleven = mode === 'eleven';
    btnOpen?.setAttribute('aria-pressed', String(eleven));
    btnClose?.setAttribute('aria-pressed', String(!eleven));
    demoPanel?.classList.toggle('is-eleven', eleven);

    if (statusEl) {
      statusEl.textContent = eleven ? 'Fully Open — 11 Mode' : 'ECU Controlled';
    }

    if (eleven) {
      demo.target = 11;
    } else {
      demo.target = rand(4, 7);
      demo.nextWanderAt = performance.now() + rand(1200, 2400);
    }

    // Mid-playback mode swap: hand off to the other clip (the remote press is
    // a user gesture, so play() is allowed here on mobile too).
    if (sound.playing) playSound();
    else setSoundUI();

    if (prefersReducedMotion) {
      demo.volume = demo.target;
      renderDemo(true);
    }
  }

  function renderDemo(force) {
    const lit = clamp(Math.round(demo.volume), 0, 11);

    if (force || lit !== demo.lastLit) {
      for (const seg of segs) {
        const n = Number(seg.dataset.seg);
        seg.classList.toggle('is-lit', n <= lit);
      }
      demo.lastLit = lit;
    }

    if (readoutEl && (force || lit !== demo.lastReadout)) {
      readoutEl.textContent = String(lit);
      demo.lastReadout = lit;
    }
  }

  function demoLoop(now) {
    if (!demo.active) return;

    const live = soundLevel();
    if (live !== null) {
      // Real audio playing: the meter tracks the clip's loudness envelope.
      demo.volume = lerp(demo.volume, live, 0.4);
    } else if (demo.mode === 'stock') {
      // ECU logic wanders: valves partially open, never fully.
      if (now >= demo.nextWanderAt) {
        demo.target = rand(4, 7);
        demo.nextWanderAt = now + rand(1200, 2400);
      }
      demo.volume = lerp(demo.volume, demo.target, 0.045);
    } else {
      // 11 MODE: pinned wide open, fast ramp.
      demo.volume = lerp(demo.volume, 11, 0.16);
      if (demo.volume > 10.985) demo.volume = 11;
    }

    renderDemo(false);
    demo.raf = requestAnimationFrame(demoLoop);
  }

  btnOpen?.addEventListener('click', () => setMode('eleven'));
  btnClose?.addEventListener('click', () => setMode('stock'));

  // Only animate while the demo is near the viewport.
  if (demoSection && 'IntersectionObserver' in window && !prefersReducedMotion) {
    const demoIO = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const shouldRun = !!(entry && entry.isIntersecting);
      if (shouldRun && !demo.active) {
        demo.active = true;
        demo.raf = requestAnimationFrame(demoLoop);
      } else if (!shouldRun && demo.active) {
        demo.active = false;
        if (demo.raf) cancelAnimationFrame(demo.raf);
        // Don't leave an exhaust note playing from a section that scrolled away.
        if (sound.playing) stopSound();
      }
    }, { rootMargin: '160px 0px 160px 0px', threshold: 0.01 });
    demoIO.observe(demoSection);
  } else if (!prefersReducedMotion) {
    demo.active = true;
    demo.raf = requestAnimationFrame(demoLoop);
  }

  // Initial paint
  renderDemo(true);
  setSoundUI();
  computeHeroMetrics();
  updateHeroProgress();
})();
